import * as React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AppointmentData } from '../../../models';
import { getDateString, parseTimeStringToMinutes } from '../../../shared/utils';

interface Props {
    appointments: AppointmentData[];
    selectedDate: string;
    children: React.ReactNode;
}

const AppointmentsWrapper = styled.div`
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    width: 100%;
    max-width: 100%;
    overflow-x: visible;
`;

const NowMarker = styled.div<{ $top: number }>`
    position: absolute;
    left: 0;
    right: 0;
    top: ${({ $top }) => $top}px;
    height: 0;
    border-top: var(--border-medium) dashed var(--brand-secondary);
    pointer-events: none;
    z-index: 2;

    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: -5px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--brand-secondary);
    }
`;

const getOffsetTopRelativeTo = (element: HTMLElement, container: HTMLElement): number => {
    let top = 0;
    let current: HTMLElement | null = element;
    while (current && current !== container) {
        top += current.offsetTop;
        current = current.offsetParent as HTMLElement | null;
    }
    return top;
};

const findClosestAppointmentIndex = (appointments: AppointmentData[], nowMinutes: number): number => {
    if (!appointments.length) return -1;
    let closestIdx = 0;
    let minDelta = Infinity;
    appointments.forEach((a, idx) => {
        const startMinutes = parseTimeStringToMinutes(a.start);
        const delta = Math.abs(startMinutes - nowMinutes);
        if (delta < minDelta) {
            minDelta = delta;
            closestIdx = idx;
        }
    });
    return closestIdx;
};

const getCurrentDate = (): Date => new Date();

const NowCenteredAppointments: React.FC<Props> = ({ appointments, selectedDate, children }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [nowMarkerTop, setNowMarkerTop] = useState<number | null>(null);
    const [now, setNow] = useState(getCurrentDate);

    const isToday = selectedDate === getDateString(now);

    useEffect(() => {
        const updateNow = () => setNow(getCurrentDate());
        const intervalId = window.setInterval(updateNow, 60 * 1000);

        window.addEventListener('focus', updateNow);
        document.addEventListener('visibilitychange', updateNow);

        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener('focus', updateNow);
            document.removeEventListener('visibilitychange', updateNow);
        };
    }, []);

    useLayoutEffect(() => {
        if (!isToday) {
            setNowMarkerTop(null);
            return;
        }
        const container = containerRef.current;
        if (!container) return;

        const nowMinutes = now.getHours() * 60 + now.getMinutes();

        if (!appointments.length) {
            setNowMarkerTop(null);
            return;
        }

        const closestIdx = findClosestAppointmentIndex(appointments, nowMinutes);
        const closestRow = container.querySelector<HTMLElement>(`#appointment-${closestIdx}`);
        if (!closestRow) {
            setNowMarkerTop(null);
            return;
        }

        const closestStartMin = parseTimeStringToMinutes(appointments[closestIdx].start);
        let markerTop: number;
        if (nowMinutes <= closestStartMin) {
            markerTop = getOffsetTopRelativeTo(closestRow, container);
        } else {
            const nextRow = container.querySelector<HTMLElement>(`#appointment-${closestIdx + 1}`);
            markerTop = nextRow
                ? getOffsetTopRelativeTo(nextRow, container)
                : getOffsetTopRelativeTo(closestRow, container) + closestRow.offsetHeight;
        }
        setNowMarkerTop(markerTop);
    }, [appointments, isToday, now]);

    return (
        <AppointmentsWrapper ref={containerRef}>
            {isToday && nowMarkerTop !== null && <NowMarker $top={nowMarkerTop} aria-hidden />}
            {children}
        </AppointmentsWrapper>
    );
};

export default NowCenteredAppointments;
